import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useCartStore } from "../stores/cartStore";
import { formatPrice } from "../utils/currency";
import { getMenuUrl, useRestaurantId } from "../utils/restaurant";
import {
  Container,
  Title,
  Paper,
  Stack,
  Group,
  Text,
  Button,
  ActionIcon,
  Grid,
  Center,
  Divider,
  Alert,
  Loader,
  TextInput,
  Textarea,
} from "@mantine/core";
import {
  IconMinus,
  IconPlus,
  IconTrash,
  IconShoppingCart,
  IconAlertCircle,
} from "@tabler/icons-react";
import { apiClient } from "../lib/apiClient";
import { notifications } from "@mantine/notifications";
import Lottie from "lottie-react";
import checkoutAnimation from "../assets/checkout_lottie_v2.json";

export default function CartPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const items = useCartStore((state) => state.items);
  const total = useCartStore((state) => state.total);
  const updateQuantity = useCartStore((state) => state.updateQuantity);
  const removeFromCart = useCartStore((state) => state.removeFromCart);
  const clearCart = useCartStore((state) => state.clearCart);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [phone, setPhone] = useState("");
  const [notes, setNotes] = useState("");

  // Get restaurant ID (La Vida)
  const restaurantId = useRestaurantId();

  const handleUpdateQuantity = (id: string, quantity: number) => {
    if (quantity < 1) {
      removeFromCart(id);
    } else {
      updateQuantity(id, quantity);
    }
  };

  const handleRemove = (id: string) => {
    if (confirm(t("cart.confirmRemove"))) {
      removeFromCart(id);
    }
  };

  const handleCheckout = async () => {
    // Validate phone number
    if (!phone.trim()) {
      setError(t("cart.phoneRequired"));
      return;
    }

    setIsProcessing(true);
    setError(null);

    try {
      // Create order with cash payment
      const orderResponse = await apiClient<{
        id: string;
        total_amount: number;
        status: string;
        payment_status: string;
        pickup_code: string;
      }>("/orders", {
        method: "POST",
        body: JSON.stringify({
          items: items.map((item) => ({
            menu_item_id: item.id,
            quantity: item.quantity,
          })),
          payment_method: "cash",
          guest_phone: phone,
          notes: notes || undefined,
        }),
      });

      // Clear cart
      clearCart();

      // Show success notification
      notifications.show({
        title: t("cart.orderSuccess"),
        message: t("cart.orderSuccessMessage"),
        color: "green",
      });

      // Artificial delay to show the animation (since no Stripe wait anymore)
      setTimeout(() => {
        navigate(`/order-confirmation/${orderResponse.id}`);
      }, 800);
    } catch (err) {
      console.error("Checkout error:", err);
      setIsProcessing(false);

      let errorMessage = t("cart.checkoutError");

      // Extract detailed error message from API response
      if (err && typeof err === "object" && "data" in err) {
        const apiError = err as any;
        console.log("API Error Data:", apiError.data);

        if (apiError.data && typeof apiError.data === "object") {
          if ("detail" in apiError.data) {
            errorMessage = Array.isArray(apiError.data.detail)
              ? apiError.data.detail.map((d: any) => d.msg).join(", ")
              : apiError.data.detail;
          } else if ("message" in apiError.data) {
            errorMessage = apiError.data.message;
          }
        }
      } else if (err instanceof Error) {
        errorMessage = err.message;
      }

      setError(errorMessage);

      notifications.show({
        title: t("cart.checkoutErrorTitle"),
        message: errorMessage,
        color: "red",
      });
    }
  };

  // Show processing animation if checkout is in progress (even if cart is empty)
  if (isProcessing) {
    return (
      <Container py="xl">
        <Title order={1} mb="lg">
          {t("cart.title")}
        </Title>
        <Center h={400}>
          <Stack align="center" gap="md">
            <Lottie
              animationData={checkoutAnimation}
              loop
              autoplay
              style={{ width: 300, height: 300 }}
            />
            <Text size="lg" fw={500}>
              {t("cart.processingOrder")}
            </Text>
            <Text size="sm" c="dimmed">
              {t("cart.processingOrderMessage")}
            </Text>
          </Stack>
        </Center>
      </Container>
    );
  }

  // Show empty cart UI only if not processing
  if (items.length === 0) {
    return (
      <Container py="xl">
        <Title order={1} mb="lg">
          {t("cart.title")}
        </Title>
        <Center h={300}>
          <Stack align="center" gap="md" data-testid="empty-cart-message">
            <IconShoppingCart size={64} stroke={1.5} color="gray" />
            <Text c="dimmed">{t("cart.empty")}</Text>
            <Button
              component={Link}
              to={getMenuUrl(restaurantId)}
              color="brand"
            >
              {t("cart.browseMenu")}
            </Button>
          </Stack>
        </Center>
      </Container>
    );
  }

  return (
    <Container py="md">
      <Title order={1} mb="lg">
        {t("cart.title")}
      </Title>

      <Grid>
        <Grid.Col span={{ base: 12, md: 8 }}>
          <Stack gap="md">
            {items.map((item) => (
              <Paper key={item.id} withBorder p="md" data-testid="cart-item">
                <Group justify="space-between" align="flex-start" wrap="nowrap">
                  {/* Left: name, unit price, quantity controls */}
                  <Stack gap={6} style={{ flex: 1 }}>
                    <Text fw={600}>{item.name}</Text>
                    <Text size="sm" c="dimmed">
                      {formatPrice(item.price)}
                    </Text>

                    <Group gap={2} wrap="nowrap" align="center">
                      <ActionIcon
                        data-testid={`decrement-${item.id}`}
                        aria-label="Decrement quantity"
                        variant="default"
                        onClick={() =>
                          handleUpdateQuantity(item.id, item.quantity - 1)
                        }
                      >
                        <IconMinus size={16} />
                      </ActionIcon>

                      <Text
                        fw={600}
                        w={30}
                        ta="center"
                        data-testid={`quantity-${item.id}`}
                      >
                        {item.quantity}
                      </Text>

                      <ActionIcon
                        data-testid={`increment-${item.id}`}
                        aria-label="Increment quantity"
                        variant="default"
                        onClick={() =>
                          handleUpdateQuantity(item.id, item.quantity + 1)
                        }
                      >
                        <IconPlus size={16} />
                      </ActionIcon>
                    </Group>
                  </Stack>

                  {/* Right: line total + delete */}
                  <Stack gap={8} align="flex-end">
                    <Text fw={700}>
                      {formatPrice(item.price * item.quantity)}
                    </Text>
                    <ActionIcon
                      data-testid={`remove-${item.id}`}
                      aria-label="Remove item"
                      color="red"
                      variant="subtle"
                      onClick={() => handleRemove(item.id)}
                    >
                      <IconTrash size={18} />
                    </ActionIcon>
                  </Stack>
                </Group>
              </Paper>
            ))}
          </Stack>
        </Grid.Col>

        <Grid.Col span={{ base: 12, md: 4 }}>
          <Paper withBorder p="md" pos="sticky" top={80}>
            <Stack gap="md">
              <Title order={3}>{t("cart.orderSummary")}</Title>

              <Group justify="space-between">
                <Text>{t("cart.subtotal")}</Text>
                <Text>{formatPrice(total)}</Text>
              </Group>

              <Divider />

              <Group justify="space-between">
                <Text fw={700} size="lg">
                  {t("cart.total")}
                </Text>
                <Text fw={700} size="lg">
                  {formatPrice(total)}
                </Text>
              </Group>

              <Divider />

              <TextInput
                label={t("cart.phoneLabel")}
                placeholder={t("cart.phonePlaceholder")}
                value={phone}
                onChange={(e) =>
                  setPhone(e.currentTarget.value.replace(/\D/g, ""))
                }
                required
                maxLength={8}
                error={
                  !phone.trim()
                    ? error
                    : phone.length !== 8
                    ? "Telefonnummeret skal bestå af 8 cifre"
                    : undefined
                }
                inputMode="numeric"
                pattern="\d{8}"
              />

              <Textarea
                label={t("cart.notesLabel")}
                placeholder={t("cart.notesPlaceholder")}
                value={notes}
                onChange={(e) => setNotes(e.currentTarget.value)}
                minRows={2}
              />

              <Stack gap="sm">
                <Alert variant="light" color="blue" title="Afhentning og betaling">
                  Bemærk: Du skal selv hente og betale for din ordre i butikken.
                </Alert>
                {error && phone.trim() && (
                  <Alert icon={<IconAlertCircle size={16} />} color="red">
                    {error}
                  </Alert>
                )}

                <Button
                  data-testid="checkout-button"
                  onClick={handleCheckout}
                  color="brand"
                  fullWidth
                  size="lg"
                  disabled={isProcessing}
                  loading={isProcessing}
                >
                  {isProcessing ? (
                    <Group gap="xs">
                      <Loader size="sm" color="white" />
                      <span>{t("cart.processing")}</span>
                    </Group>
                  ) : (
                    t("cart.placeOrder")
                  )}
                </Button>

                <Text size="xs" c="dimmed" ta="center">
                  {t("cart.paymentInfo")}
                </Text>
              </Stack>
            </Stack>
          </Paper>
        </Grid.Col>
      </Grid>
    </Container>
  );
}
